import React, { type ReactNode } from 'react';
import { Button as ButtonRAC } from 'react-aria-components';

import { useFieldPropsV2 } from '../../contexts/field-props-context';
import { findChildOfType } from '../../services/find-child';
import { type SubmitButtonFieldProps } from '../../types/field-props';

import { ProgressCircle } from './submit-button-loader';

export type SubmitButtonProps = {
  children: ReactNode;
};

export const SubmitButton = ({ children }: SubmitButtonProps) => {
  const { id, FieldLayout } = useFieldPropsV2<SubmitButtonFieldProps>();

  const buttonsContainerElement = findChildOfType(
    children,
    SubmitButton.SubmitButton,
  );

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() => null}
      renderInput={() => <>{buttonsContainerElement}</>}
      renderDescription={() => null}
    />
  );
};

type ButtonsContainerProps = {
  className?: string;
  children: ReactNode;
};

const ButtonsContainer = ({ className, children }: ButtonsContainerProps) => {
  const previousButtonElement = findChildOfType(children, PreviousButton);
  const nextButtonElement = findChildOfType(children, NextButton);
  const submitButtonElement = findChildOfType(children, SubmitButtonComponent);

  return (
    <div className={className}>
      {previousButtonElement}
      {nextButtonElement}
      {submitButtonElement}
    </div>
  );
};

type PreviousButtonProps = {
  className?: string;
};

const PreviousButton = ({ className }: PreviousButtonProps) => {
  const { showPreviousButton, onPreviousClick, previousText } =
    useFieldPropsV2<SubmitButtonFieldProps>();

  if (!showPreviousButton) {
    return null;
  }

  return (
    <ButtonRAC className={className} onPress={onPreviousClick}>
      {previousText}
    </ButtonRAC>
  );
};

type NextButtonProps = {
  className?: string;
};

const NextButton = ({ className }: NextButtonProps) => {
  const { showNextButton, onNextClick, nextText } =
    useFieldPropsV2<SubmitButtonFieldProps>();

  if (!showNextButton) {
    return null;
  }

  return (
    <ButtonRAC className={className} onPress={onNextClick}>
      {nextText}
    </ButtonRAC>
  );
};

type SubmitButtonComponentProps = {
  className?: string;
  children?: ReactNode;
};

const SubmitButtonComponent = ({
  className,
  children,
}: SubmitButtonComponentProps) => {
  const {
    showSubmitButton,
    onSubmitClick,
    submitText,
    isDisabled,
    isInProgress,
  } = useFieldPropsV2<SubmitButtonFieldProps>();

  if (!showSubmitButton) {
    return null;
  }

  const loaderElement = findChildOfType(children, SubmitButtonComponent.Loader);

  return (
    <ButtonRAC
      className={className}
      onPress={onSubmitClick}
      isDisabled={isDisabled || isInProgress}
      data-in-progress={isInProgress}
    >
      {isInProgress && loaderElement ? loaderElement : submitText}
    </ButtonRAC>
  );
};

type LoaderProps = {
  className?: string;
};

const Loader = ({ className }: LoaderProps) => {
  return (
    <span className={className}>
      <ProgressCircle />
    </span>
  );
};

SubmitButtonComponent.Loader = Loader;

ButtonsContainer.PreviousButton = PreviousButton;
ButtonsContainer.NextButton = NextButton;
ButtonsContainer.SubmitButton = SubmitButtonComponent;

SubmitButton.SubmitButton = ButtonsContainer;
